import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import localForage from "localforage"

import { Errors, RootState, User } from "@/types"
import { fetchCurrentUser, fetchSignIn, fetchSignOut } from "@/utils"
import { getFirstError } from "@/utils/errors"

type UserState = {
  isAuthenticating: boolean
  isAuthenticated: boolean | null
  current: User | null
  errors: Errors | null
}

const initialState: UserState = {
  isAuthenticating: false,
  isAuthenticated: null,
  current: null,
  errors: null,
}

type LoginCredentials = {
  username: string
  password: string
}

export const requestUserLogin = createAsyncThunk(
  "user/requestLogin",
  async ({ username, password }: LoginCredentials) => {
    const { response, json } = await fetchSignIn(username, password)

    const token = response.headers.get("authorization")
    if (token) localForage.setItem("token", token)

    return {
      ok: response.ok,
      status: response.status,
      errors: json.errors,
    }
  }
)

export const requestUserLogout = createAsyncThunk("user/requestLogout", async (_, { dispatch }) => {
  try {
    await fetchSignOut()
  } catch (err) {
    console.log(err)
  }

  await localForage.clear()
  sessionStorage.clear()
  dispatch(clearDataOnLogout())
})

export const requestCurrentUser = createAsyncThunk("user/requestCurrentUser", async () => {
  const { response, json } = await fetchCurrentUser()

  return {
    ok: response.ok,
    status: response.status,
    data: json.data,
    // errors: json.errors,
  }
})

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setIsAuthenticated(state, action: PayloadAction<boolean>) {
      state.isAuthenticated = action.payload
    },
    clearDataOnLogout(state) {
      state.isAuthenticated = false
      state.current = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(requestUserLogin.pending, (state) => {
      state.isAuthenticating = true
    })

    builder.addCase(requestUserLogin.fulfilled, (state, { payload }) => {
      state.isAuthenticating = false
      if (payload.ok) {
        state.isAuthenticated = true
        state.errors = null
      } else if (payload.errors) {
        state.errors = payload.errors
      }
    })

    builder.addCase(requestUserLogin.rejected, (state) => {
      state.isAuthenticating = false
      state.errors = {
        server: [{ code: "server_error", text: "Błąd serwera lub połączenia" }],
      }
    })

    builder.addCase(requestCurrentUser.fulfilled, (state, { payload }) => {
      if (payload.ok) {
        state.isAuthenticated = true
        if (payload.data) state.current = payload.data
      } else {
        state.isAuthenticated = false
      }
    })
  },
})

export const selectIsAuthenticating = (state: RootState) => state.user.isAuthenticating

export const selectIsAuthenticated = (state: RootState) => state.user.isAuthenticated

export const selectCurrentUser = (state: RootState) => state.user.current

export const selectAuthError = (state: RootState) => (state.user.errors ? getFirstError(state.user.errors) : null)

export const { setIsAuthenticated, clearDataOnLogout } = userSlice.actions
export const userReducer = userSlice.reducer
